import React, { useContext, useMemo, useRef, useState } from 'react';
import { Card, CardBody, CardHeader } from '@aidkitorg/component-library';
import InterfaceContext from '../../../Context';
import { useMarkdown } from '../../../Util';
import { MapRegionDensity as MapRegionDensityComponent } from '../../../DistroDashboard/MapRegionDensity';
import type * as survey from '@aidkitorg/types/lib/survey';
import { DashboardComponentCard } from './DashboardComponents/DashboardComponentCard';
import { UnsupportedComponentPlaceholder } from './UnsupportedComponentPlaceholder';
import { PrecomputedChartCard } from './PrecomputedChartCard';
import { PaymentDashboardCard } from './PaymentDashboardCard';
import { DownloadOnlyCard } from './DownloadOnlyCard';
import { QueueComponentRenderer } from './QueueComponentRenderer';
import { DataDictionaryCard } from './DataDictionaryCard';
import { useDashboards } from '../../../Contexts/DashboardsContext';
import { isNestedComponentId } from '../hooks/useUpdateComponentById';
import { useAsyncEffect } from '../../../Hooks/AsyncEffect';
import { extractApplicantTableHeaders, type AdaptedDashboardComponent } from '../adapters/distroToAdminPanel';
import type { FilterConfig } from '@aidkitorg/types/lib/admin-panel/filters';
import type { BooleanExpr } from '@aidkitorg/types/lib/survey';


interface UniversalDashboardGridItemProps {
  adapted: AdaptedDashboardComponent;
  index: number;
  /** The component ID matching the stagedComponents entry (e.g. `${dashboardId}-${idx}`) */
  componentId?: string;
  /** Override the adapted component config (e.g. from stagedComponents after an edit) */
  componentConfigOverride?: any;
  width: number;
  height: number;
  /** Raw dashboard data needed for download cards */
  dashboardData?: survey.Dashboard | null;
  viewFilters?: FilterConfig;
  /** Saved dashboard-level filter (BooleanExpr) passed to component translation */
  dashboardFilter?: BooleanExpr;
  /** When true, adds a drag handle so the grid item can be repositioned */
  isEditMode?: boolean;
  /** When true, adds a drag handle so the grid item can be repositioned. Defaults to isEditMode. */
  isDraggable?: boolean;
  /** Static widget ID from the dashboard widget library */
  staticWidgetId?: string;
  /** Saved dashboard UUID for DataDog tagging on translate queries */
  dashboardId?: string;
  /** Current section filter selections keyed by filterPath */
  sectionFilterIndexes?: Record<string, number>;
  /** Filter-aware dashboard refresh. */
  onRefreshDashboardData?: () => Promise<void> | void;
}

/**
 * Locked placeholder shown in the builder for a component the current user cannot view
 * (its `viewableBy` excludes their tags). The backend stamps `_restricted` on these and
 * runs no query, so no data is loaded or displayed. The tile holds the component's grid
 * slot so layout/flat-index alignment is preserved and it round-trips on save.
 */
const RestrictedComponentCard: React.FC<{ component: any }> = ({ component }) => {
  const title = (() => {
    const t = component?.title;
    if (typeof t === 'string') return t;
    if (t && typeof t === 'object' && 'en' in t) return t.en;
    return component?.kind || 'Restricted widget';
  })();

  return (
    <Card className="rounded-md h-full border-dashed">
      <CardHeader
        title={
          <div className="flex flex-wrap items-center gap-2 text-sm break-normal px-4 py-2 text-gray-500">
            <span aria-hidden="true">🔒</span>
            {title}
          </div>
        }
      />
      <CardBody className="flex-1 flex">
        <div className="flex-1 flex items-center justify-center text-center text-gray-400 text-sm px-4">
          Hidden — you don't have permission to view this widget. It is preserved when you save.
        </div>
      </CardBody>
    </Card>
  );
};

/**
 * Renders markdown content for a DashboardExplanation component.
 */
const DashboardExplanationRenderer: React.FC<{
  explanation: survey.DashboardExplanation;
}> = ({ explanation }) => {
  const context = useContext(InterfaceContext);

  const content = useMemo(() => {
    const richText = explanation.content;
    if (!richText) return '';
    return richText[context.lang as keyof typeof richText] || richText.en || '';
  }, [explanation.content, context.lang]);

  const renderedMarkdown = useMarkdown(content);

  const alignment = explanation.alignment || 'center';

  return (
    <div className="w-full overflow-auto">
      <div
        className={`prose prose-sm max-w-none [&_*:first-child]:mt-0 [&_*:last-child]:mb-0 ${alignment === 'left' ? 'text-left' : 'text-center'}`}
      >
        {renderedMarkdown}
      </div>
    </div>
  );
};

/**
 * Fetches data via /load for components that have a dashboardRef but were not
 * pre-computed inline (e.g. Custom Query). Once data arrives, delegates to
 * PrecomputedChartCard for visualization-aware rendering.
 */
/**
 * Derives a PrecomputedChartCard-compatible visualization and title from the
 * original distro component and the /load result data.
 */
function buildPrecomputedData(
  original: survey.DashboardComponent,
  data: any,
  rowCount?: number | null,
): { results: any; visualization: any; title?: string; staticColumnHeaders?: string[] } | null {
  const comp = original as any;
  const kind: string = comp.kind;

  const extractTitle = (t: any): string | undefined => {
    if (!t) return undefined;
    if (typeof t === 'string') return t;
    return t.en ?? undefined;
  };

  switch (kind) {
    case 'Hero Count':
      return {
        results: rowCount ?? 0,
        visualization: {
          kind: 'Custom Number' as const,
          title: typeof comp.title === 'string' ? { en: comp.title } : comp.title,
          prefix: comp.prefix,
          suffix: comp.suffix,
          useCommas: comp.useCommas,
          decimalPlaces: comp.decimalPlaces,
        },
        title: extractTitle(comp.title),
      };
    case 'Applicant Table':
      return {
        results: data ?? [],
        visualization: {
          kind: 'Table' as const,
          title: comp.title,
          hideViewButton: comp.hideViewButton,
          hideTotalCount: comp.hideTotalCount,
          hideUidCopyButton: comp.hideUidCopyButton,
          linksToSection: comp.linksToSection,
        },
        title: extractTitle(comp.title || 'Applicant Table'),
        staticColumnHeaders: extractApplicantTableHeaders(comp.columns, { hideUidCopyButton: comp.hideUidCopyButton }),
      };
    case 'Custom Query': {
      const viz = comp.visualization;
      return {
        results: data ?? [],
        visualization: viz,
        title: extractTitle(viz && 'title' in viz ? viz.title : undefined),
      };
    }
    default:
      return null;
  }
}

export const DeferredDataCard: React.FC<{
  original: survey.DashboardComponent;
  componentId: string;
  dashboardId?: string;
  viewFilters?: FilterConfig;
  dashboardFilter?: BooleanExpr;
  width: number;
  height: number;
  staticWidgetId?: string;
  isEditMode?: boolean;
  onDelete?: () => void;
  download?: { filename?: string; downloadOnly?: boolean; asyncDownload?: boolean; downloadPath?: string };
  dashboardData?: survey.Dashboard | null;
}> = ({ original, componentId, dashboardId, viewFilters, dashboardFilter, width, height, staticWidgetId, isEditMode, onDelete, download, dashboardData }) => {
  const context = useContext(InterfaceContext);
  const { loadDashboardComponentData, dashboardsById } = useDashboards();
  const forceRefreshKey = dashboardId ? dashboardsById[dashboardId]?.forceRefreshKey : undefined;
  const prevForceRefreshKeyRef = useRef(forceRefreshKey);
  const [precomputedData, setPrecomputedData] = useState<{
    results: any;
    visualization: any;
    title?: string;
    staticColumnHeaders?: string[];
  } | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<number | undefined>();

  const fetchData = async (skipCache?: boolean, signal?: AbortSignal) => {
    setLoading(true);
    setError(null);
    try {
      const result = await loadDashboardComponentData(
        original as any,
        viewFilters,
        dashboardFilter,
        { componentId, dashboardId, ...(skipCache ? { skipCache: true } : {}) }
      );
      // Drop this response if a newer effect run has superseded it. The load resolves its
      // dashboardRef from dashboardsById at call time and can't be cancelled, so a slower
      // stale load must not overwrite a fresher one.
      if (signal?.aborted) return;
      if (result?.success && result.data) {
        const built = buildPrecomputedData(original, result.data.data, result.data.rowCount);
        if (built) {
          setPrecomputedData(built);
          setLastUpdated(result.data.lastUpdated);
        } else {
          setError(`Unsupported component kind for deferred loading: ${(original as any).kind}`);
        }
      } else {
        setError(result?.errorMessage || 'Failed to load component data');
      }
    } catch (e: any) {
      if (signal?.aborted) return;
      console.error('[DeferredDataCard] Error loading component:', e);
      setError(e?.message || 'Failed to load component data');
    } finally {
      if (!signal?.aborted) setLoading(false);
    }
  };

  useAsyncEffect(async (signal) => {
    const isForceRefresh = prevForceRefreshKeyRef.current !== undefined && forceRefreshKey !== prevForceRefreshKeyRef.current;
    prevForceRefreshKeyRef.current = forceRefreshKey;
    await fetchData(isForceRefresh, signal);
  }, [componentId, dashboardId, forceRefreshKey, viewFilters, dashboardFilter, (original as any)?.dashboardRef, (original as any)?.dashboardRefs]);

  return (
    <PrecomputedChartCard
      data={precomputedData}
      loading={loading}
      error={error}
      width={width}
      height={height}
      staticWidgetId={staticWidgetId}
      isEditMode={isEditMode}
      onDelete={onDelete}
      download={download}
      dashboard={dashboardData ?? undefined}
      lang={context.lang}
      originalComponent={original}
      onRefresh={() => fetchData(true)}
      lastUpdated={lastUpdated}
    />
  );
};

/**
 * Universal grid item renderer that handles all distro dashboard component types
 * within a DashboardGrid cell.
 */
export const UniversalDashboardGridItem: React.FC<UniversalDashboardGridItemProps> = ({
  adapted,
  index,
  componentId,
  componentConfigOverride,
  width,
  height,
  dashboardData,
  viewFilters,
  dashboardFilter,
  isEditMode,
  isDraggable,
  staticWidgetId,
  dashboardId,
  sectionFilterIndexes,
  onRefreshDashboardData,
}) => {
  const context = useContext(InterfaceContext);
  const { setStagedComponents, stagedComponents } = useDashboards();
  const kind = adapted.audit.componentKind;

  // TODO: nested-widget delete; hide the action until the flat filter can handle it.
  const isNested = componentId ? isNestedComponentId(stagedComponents, componentId) : false;
  const handleDelete = componentId && !isNested
    ? () => setStagedComponents((prev) => prev.filter((sc) => sc.id !== componentId))
    : undefined;

  // Compute the effective dashboard filter, combining the parent section's active filter if applicable
  const effectiveDashboardFilter = useMemo(() => {
    const parentPath = adapted.parentSectionFilterPath;
    const parentFilters = adapted.parentSectionFilters;
    if (!parentPath || !parentFilters?.length || !sectionFilterIndexes) return dashboardFilter;

    const activeIdx = sectionFilterIndexes[parentPath] ?? 0;
    const sectionFilter = parentFilters[activeIdx]?.filter;
    if (!sectionFilter) return dashboardFilter;

    // Combine with existing dashboard filter using And
    if (dashboardFilter) {
      return { kind: 'And' as const, clauses: [dashboardFilter, sectionFilter] };
    }
    return sectionFilter;
  }, [adapted.parentSectionFilterPath, adapted.parentSectionFilters, sectionFilterIndexes, dashboardFilter]);

  // Components the user cannot view (their `viewableBy` excludes the user's tags) arrive
  // as locked placeholders flagged `_restricted`. Render a locked tile and never mount a
  // data-loading renderer, so no restricted data is fetched or displayed.
  const isRestricted = Boolean((componentConfigOverride as any)?._restricted ?? (adapted.original as any)?._restricted);
  if (isRestricted) {
    // In view, just hide the component
    if (!isEditMode) return null;
    // In edit mode, show the restricted component card
    return <RestrictedComponentCard component={componentConfigOverride || adapted.original} />;
  }

  // Adapted components (Hero Number, Bar Chart, Pie Chart, etc.) —
  // DashboardComponentCard has its own built-in drag handle, so return it directly.
  // Use componentConfigOverride (from stagedComponents) if available so edits are reflected.
  if (adapted.adapted !== null) {
    return (
      <DashboardComponentCard
        id={componentId || `distro-${index}`}
        isEditMode={isEditMode}
        isDraggable={isDraggable ?? isEditMode}
        component={componentConfigOverride || adapted.adapted}
        width={width}
        height={height}
        viewFilters={viewFilters}
        dashboardFilter={effectiveDashboardFilter}
        staticWidgetId={staticWidgetId}
        dashboardId={dashboardId}
      />
    );
  }

  // For all other component types, determine content then wrap with drag handle if editing.
  let content: React.ReactNode;

  // Dashboard Explanation - markdown content block
  if (kind === 'Dashboard Explanation') {
    content = <DashboardExplanationRenderer explanation={adapted.original as survey.DashboardExplanation} />;
  }
  // Queue - renders using QueueComponentRenderer
  else if (kind === 'Queue') {
    content = (
      // No overflow-auto: lets an embedded applicants table scroll within the cell.
      <div className="h-full w-full min-h-0 flex flex-col">
        <QueueComponentRenderer
          queue={adapted.original as survey.Queue}
          componentId={componentId || `distro-${index}`}
          dashboardId={dashboardId}
          viewFilters={viewFilters}
          dashboardFilter={effectiveDashboardFilter}
          onRefreshDashboardData={onRefreshDashboardData}
        />
      </div>
    );
  }
  // Data Dictionary - renders download button for CSV/JSON export
  else if (kind === 'Data Dictionary') {
    content = (
      <div className="h-full w-full">
        <DataDictionaryCard
          component={adapted.original as survey.DataDictionary}
          staticWidgetId={staticWidgetId}
          isEditMode={isEditMode}
          onDelete={handleDelete}
        />
      </div>
    );
  }

  // Download-only Applicant Table or Custom Query Table
  else if (kind === 'Download' && !adapted.precomputedData) {
    const orig = adapted.original as any;
    const download = orig.download || orig.visualization?.download;
    const title = orig.title || orig.visualization?.title;
    if (download?.downloadOnly && dashboardData) {
      content = (
        <div className="h-full w-full">
          <DownloadOnlyCard
            component={{ ...orig, download, title }}
            dashboard={dashboardData}
            lang={context.lang}
            staticWidgetId={staticWidgetId}
            isEditMode={isEditMode}
            onDelete={handleDelete}
          />
        </div>
      );
    }
  }
  // Map Region Density - choropleth map with precomputed data
  else if (kind === 'Map Region Density' && adapted.precomputedData) {
    const mapComponent = adapted.original as survey.MapRegionDensity;
    const geoJSON = (adapted.precomputedData.results as any[])?.[0]?.geojson;

    content = (
      <div className="h-full w-full">
        <MapRegionDensityComponent
          {...mapComponent}
          geoJSON={geoJSON}
          width={width}
          height={height}
        />
      </div>
    );
  }
  // Payment Dashboard - payment status table with precomputed data
  else if (kind === 'Payment Dashboard' && adapted.precomputedData) {
    content = (
      <PaymentDashboardCard
        component={adapted.original as survey.PaymentDashboard}
        results={adapted.precomputedData.results as any[]}
        title={adapted.precomputedData.title}
        staticWidgetId={staticWidgetId}
        isEditMode={isEditMode}
        onDelete={handleDelete}
      />
    );
  }
  // Precomputed data (CustomQuery charts, HeroCount, ApplicantTable)
  else if (adapted.precomputedData) {
    content = (
      <div className="h-full w-full">
        <PrecomputedChartCard
          data={adapted.precomputedData}
          width={width}
          height={height}
          staticWidgetId={staticWidgetId}
          isEditMode={isEditMode}
          onDelete={handleDelete}
          download={adapted.download}
          dashboard={dashboardData ?? undefined}
          lang={context.lang}
          originalComponent={adapted.original}
        />
      </div>
    );
  }

  // Deferred loading: component has a dashboardRef/dashboardRefs but no precomputed data.
  // Fetch results via /load and render with PrecomputedChartCard.
  if (!content && ((adapted.original as any)?.dashboardRef || (adapted.original as any)?.dashboardRefs)) {
    content = (
      <div className="h-full w-full">
        <DeferredDataCard
          original={adapted.original as survey.DashboardComponent}
          componentId={componentId || `distro-${index}`}
          dashboardId={dashboardId}
          viewFilters={viewFilters}
          dashboardFilter={effectiveDashboardFilter}
          width={width}
          height={height}
          staticWidgetId={staticWidgetId}
          isEditMode={isEditMode}
          onDelete={handleDelete}
          download={adapted.download}
          dashboardData={dashboardData}
        />
      </div>
    );
  }

  // Unsupported component placeholder (fallback)
  if (!content) {
    content = (
      <UnsupportedComponentPlaceholder
        component={adapted.original}
        audit={adapted.audit}
        staticWidgetId={staticWidgetId}
        isEditMode={isEditMode}
        onDelete={handleDelete}
      />
    );
  }

  // When draggable, make the whole card a drag handle so react-grid-layout can pick it up
  if (isDraggable ?? isEditMode) {
    return (
      <div className="drag-handle cursor-move h-full w-full">
        {content}
      </div>
    );
  }

  return <>{content}</>;
};
